import { cast, flow, getRoot, getSnapshot, types } from 'mobx-state-tree';
import isEqual from 'lodash/isEqual';
import clone from 'lodash/clone';
import { Crystal } from 'http-requests';
import { RequestStateModel } from '../../general-models';
import { mergeAddressMappings } from '../utils/mapping-builders';
import { IConstantStore } from '../../constantsStore';

const AddressMappingCategoryModel = types
  .model('AddressMappingCategoryModel', {
    category: types.optional(types.string, ''),
    category_id: types.optional(types.string, ''),
    manual_org_ids: types.optional(types.array(types.string), []),
  })
  .actions((self) => ({
    changeManualOrgIds(orgIds: string[] | null) {
      self.manual_org_ids = cast(orgIds ? orgIds : []);
    },
  }));

const AddressMappingModel = types
  .model('AddressMappingModel', {
    directory_house_id: types.optional(types.maybeNull(types.string), null),
    house_raw: types.optional(types.string, ''),
    system_address: types.optional(types.maybeNull(types.string), null),
    system_address_id: types.optional(types.maybeNull(types.string), null),
    routing_mode: types.optional(types.string, 'default'),
    categories: types.optional(types.array(AddressMappingCategoryModel), []),
  })
  .actions((self) => ({
    changeField<K extends keyof Omit<typeof self, 'categories'>>(
      field: K,
      value: typeof self[K]
    ) {
      self[field] = value;

      /** При приведении routing_mode в default, все manual_org_ids устанавливаются в null */
      if (field === 'routing_mode' && value === 'default') {
        self.categories = cast(
          clone(self.categories).map((category) => ({
            ...category,
            manual_org_ids: [],
          }))
        );
      }
    },
  }));

export const AddressMappingsModel = types
  .model('AddressMappingsModel', {
    mappings: types.optional(types.array(AddressMappingModel), []),
    editedMappings: types.optional(types.array(AddressMappingModel), []),
    isEdit: types.optional(types.boolean, false),
    status: types.optional(RequestStateModel, {}),
  })
  .views((self) => ({
    get isModified() {
      return !isEqual(
        getSnapshot(self.mappings),
        getSnapshot(self.editedMappings)
      );
    },
    get data() {
      return self.isEdit ? self.editedMappings : self.mappings;
    },
  }))
  .actions((self) => {
    const syncEditedMappings = () => {
      self.editedMappings = cast(getSnapshot(self.mappings));
    };

    return {
      startEdit() {
        self.isEdit = true;
        syncEditedMappings();
      },
      cancelEdit() {
        self.isEdit = false;
        syncEditedMappings();
      },
      hydrate(
        houses: Crystal.DirectoryDTO['houses'],
        mappingsResponse: Crystal.HouseMappingDTO
      ) {
        self.mappings = cast(
          mergeAddressMappings({
            houses,
            mappings: mappingsResponse.mappings,
            categories:
              getRoot<{ constants: IConstantStore }>(self).constants?.crystal
                ?.SignalsCategories || [],
          })
        );

        syncEditedMappings();
      },
      clear() {
        self.isEdit = false;
        self.mappings.clear();
        syncEditedMappings();
      },
      save: flow(function* (id: string) {
        if (!self.isModified) {
          return;
        }

        try {
          self.status.loadingStates();

          // Поиск удаленных элементов
          const deletedMappings = self.mappings.filter(
            (
              mapping
            ): mapping is typeof mapping & { directory_house_id: string } =>
              mapping.directory_house_id !== null &&
              !self.editedMappings.some(
                (edited) =>
                  edited.directory_house_id === mapping.directory_house_id
              )
          );

          const deletedIds = new Set(
            deletedMappings.map((m) => m.directory_house_id)
          );

          // Для обновления/создания (исключая удаленные)
          const upsertPayloads = self.editedMappings
            .filter(
              (
                mapping
              ): mapping is typeof mapping & { directory_house_id: string } => {
                // Пропускаем если нет ID или это удаленный элемент
                if (!mapping.directory_house_id) {
                  return false;
                }

                if (deletedIds.has(mapping.directory_house_id)) {
                  return false;
                }

                const defaultValue = self.mappings.find(
                  (def) => def.directory_house_id === mapping.directory_house_id
                );

                return !isEqual(defaultValue, mapping);
              }
            )
            .map((mapping) => ({
              house_id: mapping.directory_house_id,
              data: {
                house_raw: mapping.house_raw,
                routing_mode: mapping.routing_mode,
                system_address: mapping.system_address ?? '',
                system_address_id: mapping.system_address_id ?? '',
                map: (mapping.categories || []).map((item) => ({
                  category: item.category,
                  org_ids: item.manual_org_ids,
                })),
              },
            }));

          // Для удаленных
          const deletePromises = deletedMappings.map((mapping) =>
            Crystal.SetHouseCategoryMappings(id, mapping.directory_house_id, {
              house_raw: mapping.house_raw,
              routing_mode: 'default',
              system_address: '',
              system_address_id: '',
              map: [],
            })
          );

          const upsertPromises = upsertPayloads.map((payload) =>
            Crystal.SetHouseCategoryMappings(id, payload.house_id, payload.data)
          );

          yield Promise.all([...deletePromises, ...upsertPromises]);

          self.mappings = cast(getSnapshot(self.editedMappings));
          syncEditedMappings();
          self.status.successStates();
        } catch (error) {
          console.error(error);
          self.status.errorStates();

          return Promise.reject(error);
        } finally {
          self.status.finallyStates();
        }
      }),
    };
  });
